import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

import { IROH_SERVICES_API_PREFIX } from "@/api/domain"
import { Tag } from "@/api/tags"
import type {
  BackendBillingPlanName,
  BillingPeriod,
  PromoCodeInfo,
} from "@/billing/billing"
import type {
  StripeInvoice,
  StripeInvoiceSettings,
  StripePaymentMethod,
  StripeSubscription,
} from "@/billing/stripe"
import type { ProjectIdParam } from "@/types"
import { formatDateWithoutMillsecondsAndZ } from "@/util"

type UsageParams = {
  meter_event: string
  start: Date
  end: Date
}

type UsageInfo = {
  aggregated_value: number
}

export type ChangeSubscriptionRequest = {
  plan: BackendBillingPlanName
  billing_period: BillingPeriod
  promo_code_id?: string
  payment_method?: string
}

export type SpendCapResponse = {
  spend_cap_cents: number | null
  spend_cap_reached: boolean
  current_spend_cents: number | null
}

export const billingApi = createApi({
  reducerPath: "billingApi",
  tagTypes: [Tag.SpendCap],
  baseQuery: fetchBaseQuery({
    baseUrl: `${IROH_SERVICES_API_PREFIX}billing`,
    mode: "cors",
    credentials: "include",
  }),
  endpoints: (build) => ({
    billingGetStripeKey: build.query<string, void>({
      query: () => ({
        url: "stripe-key",
      }),
    }),
    // List all payment methods of this user.
    billingGetPaymentMethod: build.query<StripePaymentMethod[], ProjectIdParam>(
      {
        query: ({ project_id }) => ({
          url: `${project_id}/payment-method`,
        }),
      },
    ),
    // Get the current subscription for this user.
    billingGetSubscription: build.query<StripeSubscription, ProjectIdParam>({
      query: ({ project_id }) => ({
        url: `${project_id}/subscription`,
      }),
    }),
    billingGetSubscriptionUpcomingInvoice: build.query<
      StripeInvoice,
      ProjectIdParam & { subscription_id: string }
    >({
      query: ({ project_id, subscription_id }) => ({
        url: `${project_id}/subscription/${subscription_id}/upcoming-invoice`,
      }),
    }),
    billingPostSubscription: build.mutation<
      StripeSubscription,
      ProjectIdParam & ChangeSubscriptionRequest
    >({
      query: ({ project_id, ...data }) => ({
        url: `${project_id}/subscription`,
        method: "POST",
        body: data,
      }),
    }),
    billingGetInvoiceSettings: build.query<
      StripeInvoiceSettings,
      ProjectIdParam
    >({
      query: ({ project_id }) => ({
        url: `${project_id}/invoice-settings`,
      }),
    }),
    billingPatchInvoiceSettings: build.mutation<
      StripeInvoiceSettings,
      { payment_method_id: string } & ProjectIdParam
    >({
      query: ({ project_id, payment_method_id }) => ({
        url: `${project_id}/invoice-settings`,
        method: "PATCH",
        body: { payment_method_id },
      }),
    }),
    billingGetInvoices: build.query<
      StripeInvoice[],
      ProjectIdParam & { limit?: number; offset?: number }
    >({
      query: ({ project_id, limit = 20, offset = 0 }) => ({
        url: `${project_id}/invoices`,
        params: {
          limit,
          offset,
        },
      }),
    }),

    // TODO - not currently used by the frontend
    billingGetUsage: build.query<UsageInfo, ProjectIdParam & UsageParams>({
      query: ({ project_id, meter_event, start, end }) => ({
        url: `${project_id}/usage/${meter_event}`,
        params: {
          start: formatDateWithoutMillsecondsAndZ(start),
          end: formatDateWithoutMillsecondsAndZ(end),
        },
      }),
    }),

    billingGetPromoCode: build.query<PromoCodeInfo, { code: string }>({
      query: ({ code }) => ({
        url: "promo_code",
        params: {
          code,
        },
      }),
    }),

    billingGetSpendCap: build.query<SpendCapResponse, ProjectIdParam>({
      query: ({ project_id }) => ({
        url: `${project_id}/spend-cap`,
      }),
      providesTags: (_result, _error, { project_id }) => [
        { type: Tag.SpendCap, id: project_id },
      ],
    }),

    billingPatchSpendCap: build.mutation<
      SpendCapResponse,
      ProjectIdParam & { spend_cap_cents: number | null }
    >({
      query: ({ project_id, spend_cap_cents }) => ({
        url: `${project_id}/spend-cap`,
        method: "PATCH",
        body: { spend_cap_cents },
      }),
      invalidatesTags: (_result, _error, { project_id }) => [
        { type: Tag.SpendCap, id: project_id },
      ],
    }),

    billingAcceptPlanMigration: build.mutation<
      void,
      ProjectIdParam & { payment_method?: string }
    >({
      query: ({ project_id, payment_method }) => ({
        url: `${project_id}/plan-migration/accept`,
        method: "POST",
        body: payment_method ? { payment_method } : {},
      }),
    }),

    billingDeclinePlanMigration: build.mutation<void, ProjectIdParam>({
      query: ({ project_id }) => ({
        url: `${project_id}/plan-migration/decline`,
        method: "POST",
      }),
    }),
  }),
})

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
  useBillingGetStripeKeyQuery,
  useBillingGetPaymentMethodQuery,
  useBillingGetSubscriptionQuery,
  useBillingPostSubscriptionMutation,
  useBillingGetSubscriptionUpcomingInvoiceQuery,
  useBillingGetInvoiceSettingsQuery,
  useBillingGetInvoicesQuery,
  useBillingPatchInvoiceSettingsMutation,
  useBillingGetUsageQuery,
  useBillingGetPromoCodeQuery,
  useBillingGetSpendCapQuery,
  useBillingPatchSpendCapMutation,
  useBillingAcceptPlanMigrationMutation,
  useBillingDeclinePlanMigrationMutation,
} = billingApi
